// ---------------------------------------------------------------------------
// Fonte única dos metadados de busca do site.
//
// Contexto: até aqui TODAS as rotas serviam o mesmo <title>TAKE ME</title> e a
// mesma descrição, e o app não tinha nenhum mecanismo para trocar isso. Sem um
// título para usar, o Google acabou indexando a /shop com o título "R$ 1.700,00"
// — que é simplesmente o primeiro texto que aparece na página renderizada.
//
// Para mudar título ou descrição de qualquer página, mexa AQUI e só aqui.
// Quem aplica é <RouteSeo /> em src/routes/index.js.
// ---------------------------------------------------------------------------

// O nome canônico da marca. Aparece em todo título e no og:site_name.
// Está isolado numa constante de propósito: "Take Me" sozinho colide com a
// expressão em inglês e com uma empresa de transporte que já ocupa o
// takeme.com.br, então a forma com qualificador é o que permite o Google
// identificar o negócio. Se a marca decidir outra forma, troque só esta linha.
export const BRAND = "Take Me Vintage";

// Host canônico. O apex takeme.online já redireciona para cá.
// ⚠️ www e prod também respondem hoje; se a decisão de host mudar, isto muda
// junto com public/robots.txt e public/sitemap.xml.
export const SITE_URL = "https://www2.takeme.online";

// Imagem padrão de compartilhamento (WhatsApp, Instagram, etc.).
export const DEFAULT_IMAGE = `${SITE_URL}/assets/imgs/chapeu_vestido.jpg`;

const t = (titulo) => `${titulo} | ${BRAND}`;

// Rotas públicas — as que queremos que o Google indexe.
const PUBLIC_META = {
  "/": {
    title: `${BRAND} | Brechó de luxo, second hand e aluguel`,
    description:
      "Curadoria de moda de luxo second hand e vintage para compra e aluguel. Roupas, bolsas, sapatos e acessórios selecionados peça a peça, em São Paulo.",
  },
  "/shop": {
    title: t("Shop | Brechó de luxo online"),
    description:
      "Percorra o acervo Take Me Vintage: roupas, bolsas, sapatos e acessórios de grife selecionados peça a peça, disponíveis para compra e aluguel.",
  },
  "/novidades": {
    title: t("New in | Últimas peças que chegaram"),
    description:
      "As peças mais recentes do acervo Take Me Vintage. Moda de luxo second hand e vintage, atualizada conforme entra na curadoria.",
  },
  "/sale": {
    title: t("Sale | Peças de luxo com desconto"),
    description:
      "Peças selecionadas do acervo Take Me Vintage com preço reduzido — a mesma curadoria e o mesmo controle de qualidade do restante do acervo.",
  },
  "/around": {
    title: t("Aluguel de roupas e acessórios de luxo"),
    description:
      "Escolha, experimente, use e devolva. Aluguel de peças de luxo para casamentos, festas e ocasiões especiais, com atendimento Take Me Vintage em São Paulo.",
  },
  "/selection": {
    title: t("Selection | Personal shopper de luxo"),
    description:
      "Nossas fundadoras localizam a peça certa para você, mesmo que ela não esteja no acervo. Atendimento personalizado para viagens, festas e ocasiões especiais.",
  },
  "/brandlist": {
    title: t("Marcas | Grifes do acervo"),
    description:
      "As grifes presentes no acervo Take Me Vintage. Navegue por marca para encontrar peças de luxo second hand e vintage disponíveis para compra e aluguel.",
  },
  "/sellwithus": {
    title: t("Sell with us | Venda suas peças de luxo"),
    description:
      "Encontre novos caminhos para as peças que não fazem mais sentido no seu guarda-roupa. A gente cuida de tudo: avaliação, curadoria e venda.",
  },
  "/giveme": {
    title: t("Give Me | Doe peças e apoie causas sociais"),
    description:
      "Um novo jeito de desapegar: peças doadas pelo formato Give Me têm a venda revertida para instituições de caridade. Agende a entrega com a equipe.",
  },
  "/about": {
    // Sem o helper t(): a marca já está no título, e t() a repetiria.
    title: `Sobre a ${BRAND} | Curadoria de moda de luxo`,
    description:
      "A moda do futuro é aquela que já existe. Conheça as fundadoras, os três elementos Take Me e as peças cuja venda é revertida para causas sociais.",
  },
  "/faq": {
    title: t("Perguntas frequentes"),
    description:
      "Curadoria, condição das peças, formas de compra, aluguel e atendimento — as dúvidas mais comuns sobre a Take Me Vintage, respondidas.",
  },
  "/termos": {
    title: t("Termos de uso e política de privacidade"),
    description:
      "Termos de uso, política de privacidade e condições de compra, aluguel e consignação da Take Me Vintage.",
  },
};

// Rotas privadas / transacionais. Recebem título de verdade (ajuda o histórico do
// navegador e o relatório do Analytics) mas saem do índice: não há nada para
// ranquear nelas, e página de conta ou de checkout indexada só gasta rastreio.
// Casadas por prefixo, porque várias têm parâmetro na URL (/order/:id etc.).
const PRIVATE_META = [
  ["/conta", t("Minha conta")],
  ["/cart", t("Sacola")],
  ["/checkout", t("Checkout")],
  ["/order", t("Meu pedido")],
  ["/password", t("Redefinir senha")],
  ["/confirm", t("Confirmação de e-mail")],
];

/**
 * Metadados para um pathname.
 * Retorna null quando a rota monta o próprio SEO com dado dinâmico
 * (hoje só /product/:id, que precisa da marca e do nome da peça).
 */
export function metaForPath(pathname) {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

  if (path.startsWith("/product")) return null;

  if (PUBLIC_META[path]) return { ...PUBLIC_META[path], noindex: false };

  const privada = PRIVATE_META.find(([prefixo]) => path.startsWith(prefixo));
  if (privada) {
    return {
      title: privada[1],
      description: PUBLIC_META["/"].description,
      noindex: true,
    };
  }

  // Rota desconhecida: título e descrição da home, mas fora do índice — assim
  // uma URL digitada errada nunca vira uma página indexável a mais.
  return { ...PUBLIC_META["/"], noindex: true };
}
